import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import PageHead from "@/components/feature/PageHead";
import BDServiceCard from "@/pages/services/business-development/BDServiceCard";

const COMPANY_NAME = "NetBasis International";

const bdTitle = "Business Development";
const bdHeroOverlay = "Always your business in mind!";
const bdHeroAlt = "Businessman plans business strategies and sales channels for ICT market entry in the DACH market.";
const bdH1 = "Your strategic and operational partner for a successful market entry into the DACH region. We develop practical market strategies for foreign ICT companies and execute them successfully alongside you on the ground.";
const bdIntro = "NetBasis International supports the entire business endeavors of its manufacturing partners, whether introducing existing products, solutions, and services into new international markets or establishing new business areas and market opportunities. We generate and develop practical as well as effective business strategies, create and deliver the required implementation plans, and support our partners throughout the execution phase and, if desired, beyond.";
const benefitsTitle = "How you benefit from our __Business Development__ expertise:";
const readMore = "Read more";
const bottomClaim = "NetBasis International – drive to excellence with excellent drivers !";

const services = [
  {
    name: "Business Presence",
    image: "https://net-basis.net/Bilder/duesseldorf-media-harbor-rhine-tower-business-location.webp",
    description: "Local presence & contact: Establish a reliable base in Düsseldorf and operate in the DACH market efficiently – without the overhead of your own administrative structures.",
    to: "/en/services/business-development/business-presence",
  },
  {
    name: "Full-Service Offer",
    image: "https://net-basis.net/Bilder/ict-full-service-business-development-management.webp",
    description: "All-in-one business solution: As your local extension, we manage your entire DACH operations from A to Z, keeping your hands free for your home market.",
    to: "/en/services/business-development/full-service-offer",
  },
  {
    name: "Consulting Services",
    image: "https://net-basis.net/Bilder/ict-market-entry-business-strategy-consulting.webp",
    description: "Strategic business consulting: We evaluate your market potential in the DACH region and guide you with tailored advice from initial analysis to execution.",
    to: "/en/services/business-development/consulting-services",
  },
  {
    name: "Product Management",
    image: "https://net-basis.net/Bilder/ict-product-management-market-adaptation-modular-development.webp",
    description: "Your success in the DACH region: We precisely adapt your ICT products to local standards, regulations and competition.",
    to: "/en/services/business-development/product-management",
  },
  {
    name: "Project Management",
    image: "https://net-basis.net/Bilder/project-management-planning-control-dach-market-entry.webp",
    description: "Professional project control: We manage your complex DACH projects reliably, keeping track of deadlines, risks and budgets.",
    to: "/en/services/business-development/project-management",
  },
  {
    name: "Market Research",
    image: "https://net-basis.net/Bilder/market-analysis-business-intelligence-dach-market-entry.webp",
    description: "Data-driven business intelligence: We provide precise market research on size, trends and established local competition.",
    to: "/en/services/business-development/market-research",
  },
  {
    name: "Market Entry Strategies",
    image: "https://net-basis.net/Bilder/dach-market-entry-strategy-target-market-positioning.webp",
    description: "Strategic expansion: We evaluate your market entry options and design the path to sustainable success in the DACH region.",
    to: "/en/services/business-development/market-entry-strategies",
  },
  {
    name: "Import Management",
    image: "https://net-basis.net/Bilder/import-management-customs-clearance-dach-distribution.webp",
    description: "Your local import department: We manage your DACH logistics holistically – handling everything from customs clearance to legally secure, on-time delivery.",
    to: "/en/services/business-development/import-management",
  },
  {
    name: "International Trade",
    image: "https://net-basis.net/Bilder/international-trade-dach-local-expertise.webp",
    description: "Secure international trade: We manage your price structures, contracts and trade licenses in the DACH region – ensuring compliant and profitable B2B operations.",
    to: "/en/services/business-development/international-trade",
  },
  {
    name: "Technical Support",
    image: "https://net-basis.net/Bilder/technical-support-dach-customer-service.webp",
    description: "Fast technical assistance: We deliver qualified ICT support, reliable remote diagnostics and on-site support directly for your customers in the DACH region.",
    to: "/en/services/business-development/technical-support",
  },
];

const benefits = [
  "Identification and development of optimal sales channels and partner networks",
  "Conception of targeted marketing and advertising strategies",
  "Sound recommendations for necessary product modifications or market adaptations",
  "Initiation of sustainable product development opportunities",
  "Structured preparation of local business operations and logistics",
  "Precise planning for the realization of agreed business strategies",
  "Analysis and evaluation of potential opportunities for technology transfers, licensing cooperations, joint ventures, or mergers",
];

export default function BusinessDevelopmentEnPage() {
  return (
    <div className="min-h-screen flex flex-col">
      <PageHead
        title="Business Development | NetBasis International"
        description="Strategic market development and operational sales support for your sustainable success in the German-speaking area."
        ogUrl="https://net-basis.net/en/services/business-development"
      />
      <Header />

      <main className="flex-1 pt-20 md:pt-24">
        <section className="w-full bg-white py-4 md:py-6 lg:py-8">
          <div className="w-full max-w-[1100px] mx-auto">
            <div className="relative w-full h-[320px] md:h-[420px] lg:h-[500px] overflow-hidden">
              <img loading="lazy"
                src="https://net-basis.net/Bilder/ict-business-development-strategy-dach-market-entry.webp"
                alt={bdHeroAlt}
                title={bdTitle}
                className="absolute inset-0 w-full h-full object-cover object-top"
              />
              <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-black/20 to-black/50"></div>
              <div className="absolute inset-0 z-20 flex items-start justify-end md:justify-start pl-10 md:pl-[30%] lg:pl-[15%] pr-6 md:pr-10 pointer-events-none">
                <p
                  className="w-full text-3xl md:text-4xl lg:text-5xl font-normal text-white drop-shadow-[0_1px_4px_rgba(0,0,0,0.6)] text-right mt-2 md:mt-8 lg:mt-12 max-w-[85%] md:max-w-none animate-[bdTextSlide_12s_ease-in-out_infinite]"
                  style={{ fontFamily: '"Open Sans", sans-serif' }}
                >
                  {bdHeroOverlay}
                </p>
              </div>
            </div>
          </div>
        </section>

        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {bdTitle}
            </h2>
            <h1 className="text-foreground-950 font-semibold text-sm md:text-base leading-relaxed mb-4">
              {bdH1}
            </h1>
            <p className="text-foreground-950 leading-relaxed text-sm md:text-base">
              <strong className="text-primary-600">{COMPANY_NAME}</strong>{" "}
              {bdIntro.replace(COMPANY_NAME, "").trim()}
            </p>
          </div>
        </section>

        <section className="py-5 md:py-7 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-7xl mx-auto">
            <div className="flex flex-col gap-3 md:gap-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {services.slice(0, 3).map((svc, idx) => (
                  <BDServiceCard key={idx} name={svc.name} image={svc.image} description={svc.description} to={svc.to} readMore={readMore} />
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 md:gap-4">
                {services.slice(3, 7).map((svc, idx) => (
                  <BDServiceCard key={idx} name={svc.name} image={svc.image} description={svc.description} to={svc.to} readMore={readMore} />
                ))}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3 md:gap-4">
                {services.slice(7, 10).map((svc, idx) => (
                  <BDServiceCard key={idx} name={svc.name} image={svc.image} description={svc.description} to={svc.to} readMore={readMore} />
                ))}
              </div>
            </div>
          </div>
        </section>

        <section className="py-6 md:py-8 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto">
            <h2 className="font-heading text-lg md:text-xl font-bold text-primary-600 mb-5 md:mb-7">
              {benefitsTitle.split('__').map((part, i) =>
                i % 2 === 1 ? <em key={i}>{part}</em> : part
              )}
            </h2>
            <div className="space-y-1.5 md:space-y-2 text-foreground-950 leading-relaxed text-sm md:text-base">
              {benefits.map((benefit, idx) => (
                <div key={idx} className="flex items-start gap-2 md:gap-3">
                  <div className="w-5 h-5 flex-shrink-0 flex items-center justify-center mt-0.5">
                    <i className="ri-check-line text-primary-600 text-base md:text-lg"></i>
                  </div>
                  <p>{benefit}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="py-6 md:py-10 px-4 md:px-6 lg:px-8 bg-white">
          <div className="max-w-5xl mx-auto text-center">
            <p className="font-heading text-lg md:text-xl font-bold text-primary-600">
              {bottomClaim}
            </p>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
}